import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { loginPathFor } from '@/lib/authRedirect'

// Full-page loading spinner shown during session restoration
export function AppLoader() {
  return (
    <div className="min-h-screen bg-midnight-bg flex items-center justify-center">
      <div className="aurora-bg" aria-hidden="true" />
      <div className="relative z-10 flex flex-col items-center gap-4 animate-fade-in">
        <svg className="w-8 h-8 animate-spin text-white/70" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <span className="text-sm text-ink-400">Loading...</span>
      </div>
    </div>
  )
}

// Wrapper that redirects unauthenticated users to /login
// and forces password change when mustResetPassword is set.
// Uses per-field selectors so this component only re-renders when one of
// these three values changes — not on every authStore mutation.
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated  = useAuthStore((s) => s.isAuthenticated)
  const loading          = useAuthStore((s) => s.loading)
  const mustResetPassword = useAuthStore((s) => s.mustResetPassword)
  const location = useLocation()
  // Keep the page they were opening, so signing in lands there (UX-063).
  const loginPath = loginPathFor(`${location.pathname}${location.search}${location.hash}`)

  if (loading) return <AppLoader />
  if (!isAuthenticated) return <Navigate to={loginPath} replace />
  if (mustResetPassword) return <Navigate to={loginPath.replace(/^\/login/, '/change-password')} replace />
  return <>{children}</>
}
